import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Container,
  Paper,
  Box,
  CardMedia,
  Button,
} from "@material-ui/core";
import { useForm } from "react-hook-form";
import CompanyHeader from "./CompanyHeader";
import "../styles/styles.css";
import { useLocation, useNavigate } from "react-router-dom";
import HttpClient from "../api/HttpClient";
import { useLoading } from "../constant/LoadingContext";
import StaffFormFields from "../component/StaffFormFields";
import { makeStyles } from "@material-ui/core/styles";
import NavBar from "../component/NavBar";
import ReactToPrint from "react-to-print";
import { SnackbarContext } from "../constant/SnackbarProvider";
import moment from "moment";

interface FormInputs {
  english_name: string;
  chinese_name: string;
  english_name_alias: string;
  hkid: string;
  hkid_last_digit: string;
  date_of_birth: string;
  nationality: string;
  gender: "M" | "F" | undefined;
  marriage_status: "single" | "married" | undefined;
  chinese_address: string;
  english_address: string;
  phone_number: string;
  house_phone_number: string;
  email: string;
  bank_account_holder: string;
  bank_name: string;
  bank_account_number: string;
  emergency_contact_name: string;
  emergency_contact_relationship: string;
  emergency_contact_phone_number: string;
  emergency_contact_house_number: string;
}

const StaffInfoFormSigned: React.FC = () => {
  const classes = useStyles();
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const location = useLocation();
  const targetInfo = location.state?.targetInfo;
  const printComponentRef = useRef(null);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const Snackbar = React.useContext(SnackbarContext);
  
  const handleSendEmail = async () => {
    setLoading(true);
    await HttpClient().HandleSendEmailApi(
      {
        printComponentRef: printComponentRef,
        personalRef: null,
        handBookRef: null,
        email: targetInfo.email,
        area: targetInfo.area,
        subject: "員工個人資料表格",
        isFromStaff: true,
      },
      () => {
        Snackbar.open({
          alertProps: { severity: "success" },
          message: "電郵傳送文件成功!",
        });
        setLoading(false);
      },
      (error) => {
        Snackbar.open({
          alertProps: { severity: "error" },
          message: "電郵傳送文件失敗 " + error,
        });
        setLoading(false);
      }
    );
  };

  const {
    register,
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    reset,
    getValues,
  } = useForm<FormInputs>({
    defaultValues: {
      english_name: targetInfo?.english_name || "",
      chinese_name: targetInfo?.chinese_name || "",
      english_name_alias: targetInfo?.english_name_alias || "",
      hkid: targetInfo?.hkid || "",
      hkid_last_digit: targetInfo?.hkid_last_digit || "",
      date_of_birth: targetInfo?.date_of_birth || "",
      nationality: targetInfo?.nationality || "",
      gender: targetInfo?.gender || undefined,
      marriage_status: targetInfo?.marriage_status || undefined,
      chinese_address: targetInfo?.chinese_address || "",
      english_address: targetInfo?.english_address || "",
      phone_number: targetInfo?.phone_number ? String(targetInfo.phone_number) : "",
      house_phone_number: targetInfo?.house_phone_number
        ? String(targetInfo.house_phone_number)
        : "",
      email: targetInfo?.email || "",
      bank_account_holder: targetInfo?.bank_account_holder || "",
      bank_name: targetInfo?.bank_name || "",
      bank_account_number: targetInfo?.bank_account_number
        ? String(targetInfo.bank_account_number)
        : "",
      emergency_contact_name: targetInfo?.emergency_contact_name || "",
      emergency_contact_relationship:
        targetInfo?.emergency_contact_relationship || "",
      emergency_contact_phone_number: targetInfo?.emergency_contact_phone_number
        ? String(targetInfo.emergency_contact_phone_number)
        : "",
      emergency_contact_house_number: targetInfo?.emergency_contact_house_number
        ? String(targetInfo.emergency_contact_house_number)
        : "",
    },
  });

  const onSubmit = async (data: FormInputs) => {
    setLoading(true);
    try {
      // 處理數字欄位
      const processedData = {
        ...data,
        phone_number: data.phone_number ? Number(data.phone_number) : null,
        house_phone_number: data.house_phone_number
          ? Number(data.house_phone_number)
          : null,
        bank_account_number: data.bank_account_number
          ? String(data.bank_account_number)
          : null,
        emergency_contact_phone_number: data.emergency_contact_phone_number
          ? Number(data.emergency_contact_phone_number)
          : null,
        emergency_contact_house_number: data.emergency_contact_house_number
          ? Number(data.emergency_contact_house_number)
          : null,
      };

      const param = {
        ...processedData,
        staff_signature: targetInfo.staff_signature,
        staff_signature_date: targetInfo.staff_signature_date,
        area: targetInfo.area,
        token: targetInfo.token || null,
        brand: targetInfo.brand || "PM",
        link: targetInfo.link || null,
        isSync: false,
        onlyUpdateOnce: null,
      };

      const response = await HttpClient().UpdateStaffFormApi({
        id: targetInfo.id,
        param,
        onlyUpdateOnce: true,
      });

      if (response?.success) {
        Snackbar.open({
          alertProps: { severity: "success" },
          message: "更新成功",
        });
        setIsReadOnly(true);
        if (location.state) {
          location.state.targetInfo = {
            ...targetInfo,
            ...processedData,
          };
          reset({
            ...processedData,
            phone_number: String(processedData.phone_number || ""),
            house_phone_number: String(processedData.house_phone_number || ""),
            bank_account_number: String(
              processedData.bank_account_number || ""
            ),
            emergency_contact_phone_number: String(
              processedData.emergency_contact_phone_number || ""
            ),
            emergency_contact_house_number: String(
              processedData.emergency_contact_house_number || ""
            ),
          });
        }
      } else {
        let errorMessages: string[] = [];
        if (response?.message && typeof response?.message === "string") {
          errorMessages.push(response.message);
        }
        if (response.error && typeof response.error === "object") {
          Object.values(response.error).forEach((msgs: any) => {
            if (Array.isArray(msgs)) {
              msgs.forEach((msg: string) => errorMessages.push(msg));
            } else if (typeof msgs === "string") {
              errorMessages.push(msgs);
            }
          });
        }

        // 處理直接的字段錯誤
        const responseKeys = Object.keys(response);
        const hasDirectFieldErrors = responseKeys.some(
          (key) =>
            Array.isArray(response[key]) || typeof response[key] === "string"
        );

        if (!response.success && !response.message && hasDirectFieldErrors) {
          Object.values(response).forEach((msgs: any) => {
            if (Array.isArray(msgs)) {
              msgs.forEach((msg: string) => errorMessages.push(msg));
            } else if (typeof msgs === "string") {
              errorMessages.push(msgs);
            }
          });
        }

        const combinedErrorMsg = errorMessages.join("; ");
        Snackbar.open({
          alertProps: { severity: "error" },
          message: `更新失敗: ${combinedErrorMsg || "請檢查資料是否正確"}`,
        });
      }
    } catch (error) {
      Snackbar.open({
        alertProps: { severity: "error" },
        message: "更新失敗: " + error,
      });
    }
    setLoading(false);
  };

  return (
    <>
      <NavBar isBack={true} step={1} />
      <Box className={classes.container}>
        <Container>
          <Paper
            className={`${classes.paper} ${classes.printStyles}`}
            ref={printComponentRef}
          >
            <CompanyHeader brand={targetInfo?.brand || "PM"} />
            <Box className={classes.headerContainer}>
              <Typography variant="h6" className={classes.title}>
                員工個人資料
              </Typography>
              <Box className={`${classes.subtitle} no-print`}>已完成簽署</Box>
            </Box>

            <form onSubmit={handleSubmit(onSubmit)}>
              <StaffFormFields
                register={register}
                getValues={getValues}
                control={control}
                errors={errors}
                signature={
                  targetInfo?.staff_signature
                    ? JSON.parse(targetInfo.staff_signature)
                    : null
                }
                signatureDate={targetInfo?.staff_signature_date}
                setOpenModal={setOpenModal}
                isReadOnly={isReadOnly}
                hideButtons={true}
                dirtyFields={dirtyFields}
              />
            </form>
          </Paper>

          <Box
            className={`${classes.buttonContainer} ${
              !isReadOnly ? "editMode" : ""
            }`}
          >
            {isReadOnly ? (
              <>
                {(moment().diff(moment(targetInfo.joinDate), "days") <= 14 || targetInfo.joinDate === null) && (
                  <Button
                    startIcon={
                      <CardMedia
                        component="img"
                        image={require("../icons/edit.png")}
                        alt=""
                        className={classes.media}
                      />
                    }
                    variant="contained"
                    color={"primary"}
                    onClick={() => setIsReadOnly(false)}
                    className={classes.leftButton}
                  >
                    修改內容
                  </Button>
                )}
                <ReactToPrint
                  trigger={() => (
                    <Button
                      startIcon={
                        <CardMedia
                          component="img"
                          image={require("../icons/print.png")}
                          alt=""
                          className={classes.media}
                        />
                      }
                      variant="contained"
                      color={"primary"}
                      className={classes.button}
                    >
                      列印
                    </Button>
                  )}
                  documentTitle={"員工個人資料表格"}
                  content={() => printComponentRef.current}
                  onBeforeGetContent={() => {
                    Snackbar.open({
                      alertProps: { severity: "info" },
                      title: "員工個人資料表格",
                      message: "列印中...",
                    });
                    setTimeout(() => {
                      Snackbar.close();
                    }, 3000);
                  }}
                />

                <Button
                  startIcon={
                    <CardMedia
                      component="img"
                      image={require("../icons/sendEmail.png")}
                      alt=""
                      className={classes.media}
                    />
                  }
                  variant="contained"
                  color={"primary"}
                  onClick={handleSendEmail}
                  className={classes.leftButton}
                >
                  電郵傳送文件
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color={"primary"}
                onClick={handleSubmit(onSubmit)}
                className={classes.button}
              >
                確認修改
              </Button>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default StaffInfoFormSigned;

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: "#F5F5F5",
    padding: theme.spacing(3),
    minHeight: "100vh",
    [`@media not print and (max-width: 600px)`]: {
      padding: "24px 0",
    },
    "@media print": {
      padding: 0,
      minWidth: "100%",
      width: "210mm", // A4 width
      height: "297mm", // A4 height
      "@media screen": {
        // Override mobile styles when printing
        width: "100%",
        maxWidth: "none",
      },
    },
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    [`@media not print and (max-width: 960px)`]: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      margin: "20px 0",
    },
  },
  paper: {
    padding: "40px 5%",
    maxWidth: 1280,
    margin: "0 auto",
    position: "relative",
    borderRadius: "32px",
    boxShadow: "0px 0px 15px 0px #0000001A",
    [`@media not print and  (max-width: 960px)`]: {
      padding: "20px 5% 40px 5%",
    },
    [`@media not print and  (max-width: 600px)`]: {
      borderRadius: "16px",
    },
    "@media print": {
      width: "100%",
      minHeight: "297mm",
      padding: "10mm 20mm",
      margin: "0 auto",
      borderRadius: 0,
      boxShadow: "none",
    },
  },
  title: {
    fontSize: "20px",
    letterSpacing: "4%",
    marginBottom: theme.spacing(3),
    color: "#454C96",
    fontWeight: 500,
    marginTop: "60px",
    [`@media not print and  (max-width: 960px)`]: {
      marginTop: 0,
      marginBottom: 0,
    },
    "@media print": {
      marginTop: "10px !important",
    },
  },
  subtitle: {
    color: "#84909F",
    fontSize: "16px",
    fontWeight: 400,
    marginBottom: "30px",
    [`@media not print and  (max-width: 960px)`]: {
      marginBottom: 0,
    },
  },
  button: {
    margin: theme.spacing(1, 10),
    width: "100%",
    maxWidth: "350px",
  },
  leftButton: {
    backgroundColor: "#908DF2",
    margin: theme.spacing(1, 0),
    width: "100%",
    maxWidth: "350px",
  },
  media: {
    width: "20px",
    height: "20px",
    margin: "5px",
    objectFit: "contain",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    paddingTop: "2rem",
    "&.editMode": {
      justifyContent: "center",
    },
  },
  printStyles: {
    "@media print": {
      body: {
        WebkitPrintColorAdjust: "exact",
        fontSize: "12px",
      },
      "& .buttonContainer": {
        display: "none !important",
      },
      "& .print-container": {
        width: "100%",
        height: "100%",
        transform: "scale(0.75)",
        transformOrigin: "top left",
      },
    },
  },
  "@global": {
    "@media print": {
      "html, body": {
        margin: 0,
        padding: 0,
        width: "210mm",
        minHeight: "297mm",
      },

      ".no-print": {
        display: "none !important",
      },
    },
  },
}));
